import { useQuery } from "@apollo/client";
import { useEffect, useRef, useState } from "react";
import { GAMES_WITH_ASSIGNMENT } from "../../graphql/games";
import useMainStore from "../../store/mainState";
import { useNavigate } from "react-router-dom";
import { ReactComponent as PlayIcon } from '../../assets/icons/play.svg';
import { useTranslation } from 'react-i18next';

const avatars = [
    require("../../assets/images/avatar.png"),
    require("../../assets/images/avatar1.png"),
    require("../../assets/images/avatar2.png"),
    require("../../assets/images/avatar3.png"),
    require("../../assets/images/avatar4.png"),
    require("../../assets/images/avatar5.png"),
];

export const BigWinns = () => {
    const { t } = useTranslation();
    const { environment, labelData, user, setIsSignInOpen } = useMainStore();
    const navigate = useNavigate();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [wins, setWins] = useState<any[]>([]);
    const [newWinId, setNewWinId] = useState<string | null>(null);

    const onPlayGame = (game: any) => {
        if (!user) {
            setIsSignInOpen(true);
            return;
        }
        navigate('/game', { state: { game } });
    };

    const skipQuery = !labelData?.id || !environment?.id;
    const baseVars = { labelId: +labelData?.id, environmentId: environment?.id, isActive: true, after: 0 };

    const { data: slotsData } = useQuery(GAMES_WITH_ASSIGNMENT, {
        variables: { ...baseVars, first: 50, categoryType: 'SLOT' },
        fetchPolicy: 'cache-and-network',
        skip: skipQuery,
    });
    const { data: liveData } = useQuery(GAMES_WITH_ASSIGNMENT, {
        variables: { ...baseVars, first: 30, categoryType: 'LIVE' },
        fetchPolicy: 'cache-and-network',
        skip: skipQuery,
    });

    // Merge games into pool
    const allGamesRef = useRef<any[]>([]);
    useEffect(() => {
        const games = [
            ...(slotsData?.gamesWithAssignment?.games || []),
            ...(liveData?.gamesWithAssignment?.games || []),
        ];
        const unique = Array.from(new Map(games.map((g: any) => [g.id, g])).values());
        if (unique.length > 0) {
            allGamesRef.current = unique;
        }
    }, [slotsData, liveData]);

    // Initialize wins
    const hasInitialized = useRef(false);
    useEffect(() => {
        if (allGamesRef.current.length > 0 && !hasInitialized.current) {
            hasInitialized.current = true;
            setWins(allGamesRef.current.slice(0, 15).map((g: any) => ({
                ...g,
                _uid: `${g.id}-init-${Math.random()}`,
                _avatar: avatars[Math.floor(Math.random() * avatars.length)],
                _winAmount: `$${(Math.random() * 500 + 10).toFixed(2)}`,
            })));
        }
    }, [slotsData, liveData]);

    // Simulate new wins popping in
    useEffect(() => {
        if (!wins.length || !allGamesRef.current.length) return;

        const interval = setInterval(() => {
            const sourceWin = allGamesRef.current[Math.floor(Math.random() * allGamesRef.current.length)];
            const newWin = {
                ...sourceWin,
                _uid: `${sourceWin.id}-${Date.now()}`,
                _avatar: avatars[Math.floor(Math.random() * avatars.length)],
                _winAmount: `$${(Math.random() * 800 + 5).toFixed(2)}`,
                _isNew: true,
            };

            setNewWinId(newWin._uid);
            setWins(prev => [newWin, ...prev.slice(0, 49)]);

            if (scrollRef.current) {
                scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
            }

            setTimeout(() => setNewWinId(null), 1500);
        }, 800 + Math.random() * 1200);

        return () => clearInterval(interval);
    }, [wins.length]);

    if (!wins.length) return null;

    return (
        <section className="my-[20px]">
            <div className="flex flex-row items-center gap-[12px]">
                <div className="w-[8px] h-[8px] bg-emerald-400 rounded-full shadow-[0_0_8px_rgba(52,211,153,0.6)] animate-pulse" />
                <h1 className="text-[15px] lg:text-[20px] font-bold text-white tracking-tight">{t('mainScreen.recentBigWins')}</h1>
                <span className="text-[10px] font-bold text-emerald-400 bg-emerald-400/10 px-[8px] py-[2px] rounded-[6px] uppercase tracking-wider animate-pulse">{t('mainScreen.live')}</span>
            </div>
            <div ref={scrollRef} className="flex flex-row items-stretch gap-[10px] overflow-x-scroll scrollbar-hide mt-[14px] pb-[4px]">
                {wins.map((item: any) => (
                    <button
                        key={item._uid}
                        onClick={() => onPlayGame(item)}
                        className={`group min-w-[110px] w-[110px] rounded-[12px] bg-white/[0.04] border overflow-hidden transition-all duration-500 hover:bg-white/[0.07] hover:shadow-[0_4px_20px_rgba(0,0,0,0.3)] hover:-translate-y-[3px] flex-shrink-0 cursor-pointer text-left ${
                            item._uid === newWinId
                                ? 'border-emerald-400/40 shadow-[0_0_20px_rgba(52,211,153,0.15)] animate-[popIn_0.5s_ease-out]'
                                : 'border-white/[0.06] hover:border-white/[0.1]'
                        }`}
                    >
                        <div className="relative overflow-hidden">
                            <img src={item.iconUrl} alt="" className="w-full h-[125px] object-cover" />
                            <div className="absolute bottom-0 left-0 right-0 h-[40px] bg-gradient-to-t from-black/60 to-transparent"></div>
                            {item._uid === newWinId && (
                                <div className="absolute top-[6px] right-[6px] bg-emerald-400 text-[8px] text-black font-bold px-[5px] py-[1px] rounded-[4px] animate-pulse">
                                    NEW
                                </div>
                            )}
                            {/* Play overlay on hover */}
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 bg-black/40">
                                <div className="w-[36px] h-[36px] rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                                    <PlayIcon width="18" height="18" fill="#fff" />
                                </div>
                            </div>
                        </div>
                        <div className="p-[8px]">
                            <div className="flex flex-row items-center gap-[5px]">
                                <img src={item._avatar} alt="" className="w-[14px] h-[14px] rounded-full" />
                                <h1 className="text-[10px] font-semibold text-white/50 truncate">{item.name}</h1>
                            </div>
                            <h2 className={`text-[11px] font-bold text-center mt-[4px] transition-colors duration-500 ${
                                item._uid === newWinId ? 'text-emerald-400' : 'text-[#4DBDFF]'
                            }`}>{item._winAmount}</h2>
                        </div>
                    </button>
                ))}
            </div>

            <style>{`
                @keyframes popIn {
                    0% { opacity: 0; transform: scale(0.8) translateY(-10px); }
                    60% { transform: scale(1.05) translateY(0); }
                    100% { opacity: 1; transform: scale(1) translateY(0); }
                }
            `}</style>
        </section>
    );
};
